import * as Sentry from "@sentry/react";
import {
  useEffect,
} from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { version } from "../package.json";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const IS_PRODUCTION = import.meta.env.MODE === "production";

export function initSentry() {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `t3-books@${version}`,
    // User context set explicitly via Sentry.setUser() in context_and_index.jsx
    // sendDefaultPii omitted (defaults to false) — avoids capturing IP addresses/cookies

    integrations: [
      // React Router v6 — parameterized transaction names instead of raw URLs
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session Replay — watch user sessions before/during errors
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
        // Mask MUI tooltip/aria attributes that may contain financial data
        maskAttributes: [
          "title",
          "aria-label",
          "data-value",
          "aria-valuenow",
        ],
        // Don't capture network request/response bodies (contain financial data)
        networkDetailAllowUrls: [],
      }),
    ],

    // Enable Sentry Logs (structured logging via Sentry.logger.*)
    _experiments: { enableLogs: true },

    // Route-based trace sampling
    tracesSampler: (samplingContext) => {
      const name = samplingContext?.name || "";
      // Higher sampling for latency-sensitive routes
      if (name.includes("/reports/") || name.includes("/banking/") || name.includes("/upkeep/"))
        return IS_PRODUCTION ? 0.5 : 1.0;
      // Full sampling for admin (low traffic, high value)
      if (name.includes("/admin")) return 1.0;
      return IS_PRODUCTION ? 0.2 : 1.0;
    },

    // Session Replay: prod-only — all three envs share one DSN, so non-prod
    // replays burn the same quota with no support value
    replaysSessionSampleRate: IS_PRODUCTION ? 0.05 : 0,
    replaysOnErrorSampleRate: IS_PRODUCTION ? 1.0 : 0,

    // Distributed tracing: send trace headers to our backends
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/(dev|qa|app)\.t3books\.com/,
      /^https:\/\/us-central1-(dive-dev-f9d3f|dive-qa|arcwise-ad50c)\.cloudfunctions\.net/,
    ],

    // Filter out noise
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const message =
        error?.message || event?.exception?.values?.[0]?.value || "";

      // Ignore chunk load errors (expected after deployments)
      if (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("Loading chunk") ||
        message.includes("Loading CSS chunk") ||
        message.includes("is not a valid JavaScript MIME type") ||
        error?.name === "ChunkLoadError"
      ) {
        return null;
      }

      // Ignore Firestore permission errors on non-critical listeners
      if (
        message.includes("Missing or insufficient permissions") &&
        (message.includes("billingAccounts") ||
          message.includes("adminControlSessions") ||
          message.includes("adminAuditLog"))
      ) {
        return null;
      }

      return event;
    },

    // Ignore common browser extension and network errors
    ignoreErrors: [
      "top.GLOBALS",
      "ResizeObserver loop",
      "Failed to fetch",
      "NetworkError",
      "Load failed",
      /extensions\//i,
      /^chrome:\/\//i,
      // Injected by email-client embedded browsers / link scanners (CefSharp),
      // not application code — fires on email landing pages like /unsubscribe
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    ],

    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],
  });
}
